import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { User, Phone, Mail, ShieldCheck, Download, FileText, HeartPulse, AtSign, CalendarPlus, CalendarDays, Copy, RefreshCw, Sparkles, X, BellRing } from "lucide-react";

export default function Profile() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [waiver, setWaiver] = useState(null);
  const [template, setTemplate] = useState(null);

  const [contact, setContact] = useState({ name: "", phone: "" });
  const [emergency, setEmergency] = useState({ emergency_contact_name: "", emergency_contact_phone: "" });
  const [dob, setDob] = useState("");
  const [skills, setSkills] = useState([]);
  const [skillDraft, setSkillDraft] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [emailSent, setEmailSent] = useState("");
  const [calendar, setCalendar] = useState(null);
  const [calendarStep, setCalendarStep] = useState("choose"); // "choose" | "google" | "apple" | "outlook"
  const [busy, setBusy] = useState({ contact: false, emergency: false, dob: false, email: false, calendar: false, skills: false });

  useEffect(() => {
    if (user) {
      setContact({ name: user.name || "", phone: user.phone || "" });
      setSkills(Array.isArray(user.skills) ? user.skills : []);
    }
  }, [user]);

  useEffect(() => {
    api.get("/waiver/me").then(({ data }) => {
      if (data?.signed) {
        setWaiver(data);
        setEmergency({
          emergency_contact_name: data.emergency_contact_name || "",
          emergency_contact_phone: data.emergency_contact_phone || "",
        });
      }
      if (data?.date_of_birth) setDob(data.date_of_birth);
    });
    api.get("/waiver/template").then(({ data }) => setTemplate(data));
    api.get("/calendar/me").then(({ data }) => setCalendar(data)).catch(() => {});
  }, []);

  const saveDob = async (e) => {
    e.preventDefault();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dob)) { toast.error("Please enter a valid date of birth."); return; }
    setBusy(b => ({ ...b, dob: true }));
    try {
      const { data } = await api.post("/waiver/dob", { date_of_birth: dob });
      // Re-fetch the waiver so age flag / paper-waiver flow reflect immediately.
      const me = await api.get("/waiver/me");
      if (me.data) setWaiver(me.data);
      if (data.is_minor) {
        toast.error("You're under 18 — please print the paper waiver and have a parent or guardian sign it in person.");
      } else {
        toast.success("Date of birth saved.");
      }
    } catch (er) { toast.error(formatApiError(er)); }
    finally { setBusy(b => ({ ...b, dob: false })); }
  };

  const copyFeed = async () => {
    if (!calendar?.feed_url) return;
    try {
      await navigator.clipboard.writeText(calendar.feed_url);
      toast.success("Calendar subscribe link copied.");
    } catch { toast.error("Couldn't copy — long-press or select the link to copy."); }
  };

  const rotateFeed = async () => {
    if (!window.confirm("Rotating breaks the old subscribe link. You'll need to re-subscribe in your calendar app. Continue?")) return;
    setBusy(b => ({ ...b, calendar: true }));
    try {
      const { data } = await api.post("/calendar/rotate");
      const webcal = data.feed_url.replace(/^https?:\/\//, "webcal://");
      setCalendar({ feed_url: data.feed_url, webcal_url: webcal });
      toast.success("New subscribe link ready.");
    } catch (er) { toast.error(formatApiError(er)); }
    finally { setBusy(b => ({ ...b, calendar: false })); }
  };

  const proceedCalendar = () => {
    if (!calendar) return;
    let url = null;
    let newTab = false;
    if (calendarStep === "google") {
      url = `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(calendar.feed_url)}`;
      newTab = true;
    } else if (calendarStep === "apple") {
      url = calendar.webcal_url;
    } else if (calendarStep === "outlook") {
      url = `https://outlook.live.com/calendar/0/addcalendar?url=${encodeURIComponent(calendar.feed_url)}&name=${encodeURIComponent("HRAAD Volunteer Shifts")}`;
      newTab = true;
    }
    if (!url) return;
    if (newTab) window.open(url, "_blank");
    else window.location.href = url;
    setCalendarStep("choose");
  };

  const saveContact = async (e) => {
    e.preventDefault();
    if (!contact.name.trim()) { toast.error("Name is required."); return; }
    setBusy(b => ({ ...b, contact: true }));
    try {
      const { data } = await api.patch("/auth/me", contact);
      setUser({ ...user, name: data.name, phone: data.phone });
      toast.success("Contact info updated.");
    } catch (er) { toast.error(formatApiError(er)); }
    finally { setBusy(b => ({ ...b, contact: false })); }
  };

  const saveEmergency = async (e) => {
    e.preventDefault();
    if (!emergency.emergency_contact_name.trim() || emergency.emergency_contact_phone.trim().length < 5) {
      toast.error("Emergency contact name and a valid phone are required.");
      return;
    }
    setBusy(b => ({ ...b, emergency: true }));
    try {
      const { data } = await api.patch("/waiver/emergency-contact", emergency);
      setWaiver(data);
      toast.success("Emergency contact updated.");
    } catch (er) { toast.error(formatApiError(er)); }
    finally { setBusy(b => ({ ...b, emergency: false })); }
  };

  const addSkill = () => {
    const next = (skillDraft || "").trim();
    if (!next) return;
    if (next.length > 60) { toast.error("Keep each skill under 60 characters."); return; }
    if (skills.some((s) => s.toLowerCase() === next.toLowerCase())) {
      setSkillDraft("");
      return;
    }
    setSkills([...skills, next]);
    setSkillDraft("");
  };

  const removeSkill = (idx) => {
    setSkills(skills.filter((_, i) => i !== idx));
  };

  const saveSkills = async () => {
    setBusy(b => ({ ...b, skills: true }));
    try {
      const { data } = await api.patch("/me/skills", { skills });
      setUser({ ...user, skills: data.skills || [] });
      setSkills(data.skills || []);
      toast.success("Skills saved — admins can now see them.");
    } catch (er) { toast.error(formatApiError(er)); }
    finally { setBusy(b => ({ ...b, skills: false })); }
  };

  const requestEmailChange = async (e) => {
    e.preventDefault();
    if (!newEmail.trim() || !newEmail.includes("@")) { toast.error("Enter a valid email address."); return; }
    setBusy(b => ({ ...b, email: true }));
    try {
      const { data } = await api.post("/auth/change-email", { new_email: newEmail.trim() });
      setEmailSent(newEmail.trim());
      setNewEmail("");
      toast.success(data.message || "Confirmation email sent.");
    } catch (er) { toast.error(formatApiError(er)); }
    finally { setBusy(b => ({ ...b, email: false })); }
  };

  const downloadWaiverPdf = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${BACKEND_URL}/api/waiver/pdf`, { credentials: "include" });
      if (!res.ok) throw new Error("failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `hraad-waiver-${user?.name?.replace(/\s+/g, "-") || "signed"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Waiver downloaded.");
    } catch (e) { toast.error("Could not download waiver."); }
  };

  const viewReferencePdf = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${BACKEND_URL}/api/waiver/reference-pdf`, { credentials: "include" });
      if (!res.ok) throw new Error("not found");
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch { toast.error("Reference PDF not available."); }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="max-w-3xl mx-auto px-5 lg:px-10 py-10">
        <div className="tag-pill tag-yellow">Your Profile</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-3 mb-2">Keep your info current.</h1>
        <p className="text-[color:var(--stone)] mb-10">Update how the shelter can reach you and who to call in an emergency.</p>

        {/* Contact info */}
        <section className="card-warm p-7 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-full bg-[color:var(--cream-2)] flex items-center justify-center">
              <User className="h-5 w-5" />
            </div>
            <div>
              <div className="font-display text-lg">Contact information</div>
              <div className="text-xs text-[color:var(--stone)]">Used for shift confirmations and reminders.</div>
            </div>
          </div>
          <form onSubmit={saveContact} className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[color:var(--stone)] ml-1">Full name</label>
              <input data-testid="profile-name-input" required value={contact.name} onChange={(e)=>setContact({...contact, name: e.target.value})} className="mt-1 w-full px-4 py-3 rounded-full border-2 border-[color:var(--border)] outline-none focus:border-[color:var(--ink)] bg-white" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[color:var(--stone)] ml-1">Phone</label>
              <input data-testid="profile-phone-input" type="tel" value={contact.phone} onChange={(e)=>setContact({...contact, phone: e.target.value})} placeholder="e.g. 541-555-0100" className="mt-1 w-full px-4 py-3 rounded-full border-2 border-[color:var(--border)] outline-none focus:border-[color:var(--ink)] bg-white" />
            </div>
            <div className="flex items-center gap-2 p-3 rounded-2xl bg-[color:var(--cream-2)]" data-testid="profile-email-locked">
              <Mail className="h-4 w-4 text-[color:var(--stone)]" />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-[color:var(--stone)]">Current email</div>
                <div className="text-sm font-semibold truncate">{user?.email}</div>
              </div>
            </div>
            <div className="flex justify-end">
              <button data-testid="profile-save-contact-btn" type="submit" disabled={busy.contact} className="btn-pill btn-primary text-sm disabled:opacity-60">
                {busy.contact ? "Saving…" : "Save contact info"}
              </button>
            </div>
          </form>
        </section>

        {/* Special skills */}
        <section className="card-warm p-7 mb-6" data-testid="profile-skills-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-full bg-[color:var(--yellow)]/50 flex items-center justify-center">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <div className="font-display text-lg">Special skills</div>
              <div className="text-xs text-[color:var(--stone)]">Anything you&rsquo;re good at — photography, yard work, event planning, Spanish, trailer towing, grooming… admins see these so they can call on the right people.</div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-3" data-testid="profile-skills-list">
            {skills.length === 0 && (
              <p className="text-sm text-[color:var(--stone)] italic">No skills added yet.</p>
            )}
            {skills.map((s, i) => (
              <span key={`${i}-${s}`} className="inline-flex items-center gap-1 pl-3 pr-1 py-1 rounded-full bg-[color:var(--ink)]/10 text-[color:var(--ink)] text-sm">
                {s}
                <button
                  type="button"
                  data-testid={`profile-skill-remove-${i}`}
                  onClick={() => removeSkill(i)}
                  className="h-6 w-6 inline-flex items-center justify-center rounded-full hover:bg-[color:var(--ink)]/15"
                  aria-label={`Remove ${s}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <input
              data-testid="profile-skill-input"
              value={skillDraft}
              onChange={(e) => setSkillDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSkill(); } }}
              placeholder="e.g. Photography, Yard work, Event planning"
              maxLength={60}
              className="flex-1 px-4 py-3 rounded-full border-2 border-[color:var(--border)] outline-none focus:border-[color:var(--ink)] bg-white"
            />
            <button
              type="button"
              data-testid="profile-skill-add-btn"
              onClick={addSkill}
              className="btn-pill btn-ghost-ink text-sm whitespace-nowrap"
            >
              Add skill
            </button>
          </div>

          <div className="flex justify-end mt-4">
            <button
              type="button"
              data-testid="profile-save-skills-btn"
              onClick={saveSkills}
              disabled={busy.skills}
              className="btn-pill btn-primary text-sm disabled:opacity-60"
            >
              {busy.skills ? "Saving…" : "Save skills"}
            </button>
          </div>
        </section>

        {user?.role === "admin" && <AdminDigestToggle user={user} setUser={setUser} />}

        {/* Change email */}
        <section className="card-warm p-7 mb-6" data-testid="profile-email-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-full bg-[color:var(--cream-2)] flex items-center justify-center">
              <AtSign className="h-5 w-5" />
            </div>
            <div>
              <div className="font-display text-lg">Change your sign-in email</div>
              <div className="text-xs text-[color:var(--stone)]">We&rsquo;ll send a confirmation link to the new address. Sessions sign out after the change.</div>
            </div>
          </div>
          {emailSent ? (
            <div className="p-4 rounded-2xl bg-[#E4EBDD] text-[#2D4A3E] text-sm" data-testid="profile-email-sent">
              A confirmation link was sent to <span className="font-semibold">{emailSent}</span>. Click it within 24 hours to complete the switch.
            </div>
          ) : (
            <form onSubmit={requestEmailChange} className="flex flex-col sm:flex-row gap-2">
              <input data-testid="profile-new-email-input" type="email" required value={newEmail} onChange={(e)=>setNewEmail(e.target.value)} placeholder="new-email@example.com" className="flex-1 px-4 py-3 rounded-full border-2 border-[color:var(--border)] outline-none focus:border-[color:var(--ink)] bg-white" />
              <button data-testid="profile-request-email-btn" type="submit" disabled={busy.email} className="btn-pill btn-primary text-sm disabled:opacity-60">{busy.email ? "Sending…" : "Send confirmation"}</button>
            </form>
          )}
        </section>

        {/* Calendar subscribe */}
        <section className="card-warm p-7 mb-6" data-testid="profile-calendar-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-full bg-[color:var(--yellow)]/60 flex items-center justify-center">
              <CalendarPlus className="h-5 w-5" />
            </div>
            <div>
              <div className="font-display text-lg">Add my shifts to my calendar</div>
              <div className="text-xs text-[color:var(--stone)]">
                <span className="inline-block bg-[color:var(--yellow)]/60 text-[color:var(--ink)] font-semibold rounded-full px-2 py-0.5 mr-1">One-time setup</span>
                After this, every new shift you sign up for shows up automatically — you never need to do this again.
              </div>
            </div>
          </div>
          {calendar?.feed_url ? (
            <>
              {calendarStep === "choose" ? (
                <>
                  <div className="grid sm:grid-cols-3 gap-2 mb-3">
                    <button
                      data-testid="profile-calendar-google-btn"
                      onClick={()=>setCalendarStep("google")}
                      className="btn-pill btn-primary text-sm inline-flex items-center justify-center gap-2 py-3"
                    >
                      <svg viewBox="0 0 24 24" className="h-4 w-4"><path fill="currentColor" d="M21.35 11.1H12v2.9h5.35c-.23 1.5-1.7 4.4-5.35 4.4-3.22 0-5.85-2.67-5.85-5.95 0-3.28 2.63-5.95 5.85-5.95 1.83 0 3.06.78 3.76 1.45l2.56-2.47C16.86 3.9 14.68 3 12 3 6.98 3 2.93 7.03 2.93 12s4.05 9 9.07 9c5.24 0 8.71-3.68 8.71-8.86 0-.6-.06-1.05-.15-1.5z"/></svg>
                      Google Calendar
                    </button>
                    <button
                      data-testid="profile-calendar-subscribe-btn"
                      onClick={()=>setCalendarStep("apple")}
                      className="btn-pill btn-ghost-ink text-sm inline-flex items-center justify-center gap-2 py-3"
                    >
                      <svg viewBox="0 0 24 24" className="h-4 w-4"><path fill="currentColor" d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>
                      Apple Calendar
                    </button>
                    <button
                      data-testid="profile-calendar-outlook-btn"
                      onClick={()=>setCalendarStep("outlook")}
                      className="btn-pill btn-ghost-ink text-sm inline-flex items-center justify-center gap-2 py-3"
                    >
                      <svg viewBox="0 0 24 24" className="h-4 w-4"><path fill="currentColor" d="M21.5 6.5v11c0 .83-.67 1.5-1.5 1.5H10v-2h9v-9L13 4l-3 2V4l3-2 8.5 4.5zM8 8h1v8H8V8zm-3 2h1v4H5v-4zm5-2h5v8h-5V8z"/></svg>
                      Outlook Calendar
                    </button>
                  </div>
                  <p className="text-xs text-[color:var(--stone)]">One-time setup — after this, every new shift you sign up for lands in your calendar automatically.</p>
                </>
              ) : (
                <div data-testid="profile-calendar-preview" className="rounded-2xl border-2 border-[color:var(--border)] p-5">
                  <div className="font-display text-lg mb-3">
                    {calendarStep === "google" && "Here's what will happen — Google Calendar"}
                    {calendarStep === "apple" && "Here's what will happen — Apple Calendar"}
                    {calendarStep === "outlook" && "Here's what will happen — Outlook Calendar"}
                  </div>
                  <div className="rounded-2xl bg-[color:var(--cream-2)] p-4 text-sm space-y-3 mb-4">
                    {calendarStep === "google" && (
                      <>
                        <div className="flex gap-3"><span className="font-display text-xl leading-none text-[color:var(--red)]">1.</span><span>A new tab opens on google.com.</span></div>
                        <div className="flex gap-3"><span className="font-display text-xl leading-none text-[color:var(--red)]">2.</span><span>Google asks <span className="font-semibold">&ldquo;Do you want to add this calendar?&rdquo;</span> — tap <span className="font-semibold">Add</span>.</span></div>
                        <div className="flex gap-3"><span className="font-display text-xl leading-none text-[color:var(--red)]">3.</span><span>Done. Your shifts show up in Google Calendar within a few minutes.</span></div>
                      </>
                    )}
                    {calendarStep === "apple" && (
                      <>
                        <div className="flex gap-3"><span className="font-display text-xl leading-none text-[color:var(--red)]">1.</span><span>The Calendar app opens on your iPhone or Mac.</span></div>
                        <div className="flex gap-3"><span className="font-display text-xl leading-none text-[color:var(--red)]">2.</span><span>It shows a box that says <span className="font-semibold">&ldquo;Subscribe to Calendar&rdquo;</span> with a long web address — <span className="font-semibold">just tap Subscribe.</span> You don&rsquo;t need to change anything.</span></div>
                        <div className="flex gap-3"><span className="font-display text-xl leading-none text-[color:var(--red)]">3.</span><span>Tap <span className="font-semibold">Add</span> or <span className="font-semibold">Done</span> on the next screen. That&rsquo;s it — your shifts are in your calendar.</span></div>
                        <div className="mt-2 p-3 rounded-xl bg-[color:var(--yellow)]/40 text-xs">
                          <span className="font-semibold">Already subscribed?</span> If your calendar says <em>&ldquo;this subscription already exists&rdquo;</em>, you&rsquo;re all set — new shifts arrive automatically within an hour. No need to re-add.
                        </div>
                      </>
                    )}
                    {calendarStep === "outlook" && (
                      <>
                        <div className="flex gap-3"><span className="font-display text-xl leading-none text-[color:var(--red)]">1.</span><span>A new tab opens on outlook.com.</span></div>
                        <div className="flex gap-3"><span className="font-display text-xl leading-none text-[color:var(--red)]">2.</span><span>Outlook may ask you to sign in, then shows an <span className="font-semibold">Import</span> button — tap it.</span></div>
                        <div className="flex gap-3"><span className="font-display text-xl leading-none text-[color:var(--red)]">3.</span><span>Done. Your shifts show up in Outlook within a few minutes.</span></div>
                      </>
                    )}
                  </div>
                  <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                    <button data-testid="profile-calendar-back" onClick={()=>setCalendarStep("choose")} className="btn-pill btn-ghost-ink text-sm">Back</button>
                    <button data-testid="profile-calendar-proceed" onClick={proceedCalendar} className="btn-pill btn-primary text-sm">Got it, open my calendar</button>
                  </div>
                </div>
              )}
              <details className="mt-4" data-testid="profile-calendar-advanced">
                <summary className="text-xs font-semibold text-[color:var(--stone)] cursor-pointer hover:text-[color:var(--ink)]">Advanced options</summary>
                <div className="mt-3 space-y-2">
                  <div className="flex items-center gap-2 p-3 rounded-2xl bg-[color:var(--cream-2)]" data-testid="profile-calendar-feed">
                    <input readOnly value={calendar.feed_url} className="flex-1 bg-transparent text-xs font-mono truncate outline-none" onFocus={(e)=>e.target.select()} />
                    <button data-testid="profile-calendar-copy-btn" onClick={copyFeed} className="btn-pill btn-ghost-ink text-xs inline-flex items-center gap-1 shrink-0">
                      <Copy className="h-3.5 w-3.5" /> Copy link
                    </button>
                  </div>
                  <button data-testid="profile-calendar-rotate-btn" onClick={rotateFeed} disabled={busy.calendar} className="btn-pill text-xs text-[color:var(--red)] hover:bg-[color:var(--red)]/10 inline-flex items-center gap-2 disabled:opacity-60">
                    <RefreshCw className="h-3.5 w-3.5" /> {busy.calendar ? "Rotating…" : "Reset this link"}
                  </button>
                  <p className="text-xs text-[color:var(--stone)]">Keep this link private — anyone with it can see your shift schedule.</p>
                </div>
              </details>
            </>
          ) : (
            <div className="text-sm text-[color:var(--stone)]">Loading your calendar link…</div>
          )}
        </section>

        {/* Date of birth — immutable once set */}
        <section className="card-warm p-7 mb-6" data-testid="profile-dob-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-full bg-[color:var(--yellow)]/40 flex items-center justify-center">
              <CalendarDays className="h-5 w-5" />
            </div>
            <div>
              <div className="font-display text-lg">Date of birth</div>
              <div className="text-xs text-[color:var(--stone)]">
                {dob
                  ? "On file. Contact a shelter admin if you need to correct a typo — you can't change this yourself."
                  : "Required for our records. Volunteers under 18 must sign a paper waiver in person."}
              </div>
            </div>
          </div>
          <form onSubmit={saveDob} className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[color:var(--stone)] ml-1">Your date of birth</label>
              <input
                data-testid="profile-dob-input"
                type="date"
                required
                value={dob}
                max={new Date().toISOString().split("T")[0]}
                readOnly={!!dob}
                disabled={!!dob}
                onChange={(e) => setDob(e.target.value)}
                className={`mt-1 w-full px-4 py-3 rounded-full border-2 border-[color:var(--border)] outline-none focus:border-[color:var(--ink)] bg-white ${dob ? "cursor-not-allowed opacity-70" : ""}`}
              />
              {waiver?.age >= 0 && waiver?.age < 18 && (
                <p className="mt-2 ml-1 text-xs text-[color:var(--red)]" data-testid="profile-dob-minor-note">
                  On file as under 18. A parent or guardian must sign a paper waiver at 3910 Heron Drive before you can sign up for shifts.
                </p>
              )}
              {waiver?.age >= 18 && (
                <p className="mt-2 ml-1 text-xs text-[color:var(--stone)]" data-testid="profile-dob-adult-note">
                  On file: age {waiver.age}. You&rsquo;re good to sign up for shifts online.
                </p>
              )}
            </div>
            {!dob && (
              <div className="flex justify-end">
                <button data-testid="profile-save-dob-btn" type="submit" disabled={busy.dob} className="btn-pill btn-primary text-sm disabled:opacity-60">
                  {busy.dob ? "Saving…" : "Save date of birth"}
                </button>
              </div>
            )}
          </form>
        </section>

        {/* Emergency contact */}
        <section className="card-warm p-7 mb-6" data-testid="profile-emergency-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-full bg-[color:var(--red)]/15 flex items-center justify-center text-[color:var(--red)]">
              <HeartPulse className="h-5 w-5" />
            </div>
            <div>
              <div className="font-display text-lg">Emergency contact</div>
              <div className="text-xs text-[color:var(--stone)]">Who we call if something happens during your shift.</div>
            </div>
          </div>
          {waiver ? (
            <form onSubmit={saveEmergency} className="space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[color:var(--stone)] ml-1">Contact name</label>
                <input data-testid="profile-emergency-name-input" required value={emergency.emergency_contact_name} onChange={(e)=>setEmergency({...emergency, emergency_contact_name: e.target.value})} className="mt-1 w-full px-4 py-3 rounded-full border-2 border-[color:var(--border)] outline-none focus:border-[color:var(--ink)] bg-white" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[color:var(--stone)] ml-1">Contact phone</label>
                <input data-testid="profile-emergency-phone-input" type="tel" required value={emergency.emergency_contact_phone} onChange={(e)=>setEmergency({...emergency, emergency_contact_phone: e.target.value})} className="mt-1 w-full px-4 py-3 rounded-full border-2 border-[color:var(--border)] outline-none focus:border-[color:var(--ink)] bg-white" />
              </div>
              <div className="flex justify-end">
                <button data-testid="profile-save-emergency-btn" type="submit" disabled={busy.emergency} className="btn-pill btn-primary text-sm disabled:opacity-60">
                  {busy.emergency ? "Saving…" : "Save emergency contact"}
                </button>
              </div>
            </form>
          ) : (
            <div className="text-sm text-[color:var(--stone)]">
              You need to sign the waiver first — that&rsquo;s where your emergency contact gets recorded. <Link to="/waiver" className="text-[color:var(--red)] font-semibold hover:underline">Sign it now →</Link>
            </div>
          )}
        </section>

        {/* Waiver */}
        <section className="card-warm p-7">
          <div className="flex items-center gap-3 mb-4">
            <div className={`h-10 w-10 rounded-full flex items-center justify-center ${waiver ? "bg-[#E4EBDD] text-[#2D4A3E]" : "bg-[color:var(--red)]/15 text-[color:var(--red)]"}`}>
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <div className="font-display text-lg">Your waiver</div>
              <div className="text-xs text-[color:var(--stone)]">{waiver ? `Signed on ${new Date(waiver.signed_at).toLocaleDateString()}` : "Not signed yet."}</div>
            </div>
            {waiver && <span className="tag-pill tag-forest !text-[10px]">Signed</span>}
          </div>

          {waiver ? (
            <div className="space-y-4">
              <div className="p-4 rounded-2xl bg-[color:var(--cream-2)]">
                <div className="text-[10px] uppercase tracking-wider text-[color:var(--stone)]">Signed as</div>
                <div className="font-serif-display italic text-2xl mt-1">{waiver.full_name}</div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button data-testid="profile-download-waiver-btn" onClick={downloadWaiverPdf} className="btn-pill btn-ghost-ink text-sm inline-flex items-center gap-2">
                  <Download className="h-4 w-4" /> Download my signed waiver
                </button>
                {template?.has_reference_pdf && (
                  <button data-testid="profile-view-reference-btn" onClick={viewReferencePdf} className="btn-pill text-sm inline-flex items-center gap-2 text-[color:var(--red)] hover:bg-[color:var(--red)]/10">
                    <FileText className="h-4 w-4" /> View full waiver PDF
                  </button>
                )}
              </div>
              {waiver && template?.clauses && (
                <details className="mt-2" data-testid="profile-waiver-clauses">
                  <summary className="text-sm font-semibold text-[color:var(--stone)] cursor-pointer hover:text-[color:var(--ink)]">Show waiver clauses I agreed to</summary>
                  <ol className="mt-3 space-y-2 text-sm text-[color:var(--ink)] list-decimal pl-5">
                    {template.clauses.map((c, i) => <li key={`${i}-${c.slice(0,32)}`}>{c}</li>)}
                  </ol>
                </details>
              )}
            </div>
          ) : (
            <button data-testid="profile-sign-waiver-btn" onClick={()=>nav("/waiver")} className="btn-pill btn-primary text-sm">Sign the waiver</button>
          )}
        </section>
      </div>
    </div>
  );
}

function AdminDigestToggle({ user, setUser }) {
  const [busy, setBusy] = useState(false);
  const on = !!user.admin_digest_opt_in;
  const toggle = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/me/admin-digest", { enabled: !on });
      setUser({ ...user, admin_digest_opt_in: data.admin_digest_opt_in });
      toast.success(data.admin_digest_opt_in ? "Daily digest turned on — first one arrives at 7:00 AM." : "Daily digest turned off.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="card-warm p-7 mb-6" data-testid="profile-admin-digest-section">
      <div className="flex items-center gap-4">
        <div className="h-10 w-10 rounded-full bg-[color:var(--cream-2)] flex items-center justify-center shrink-0">
          <BellRing className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="font-display text-lg">Daily signup digest</div>
          <div className="text-xs text-[color:var(--stone)]">One email at 7:00 AM Pacific with the past day&rsquo;s signups &amp; cancellations, plus shifts still short of volunteers. Skipped on quiet days. You always get in-app bell alerts.</div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={on}
          data-testid="profile-admin-digest-toggle"
          onClick={toggle}
          disabled={busy}
          className={`relative h-7 w-12 rounded-full shrink-0 transition-colors disabled:opacity-60 ${on ? "bg-[color:var(--red)]" : "bg-[color:var(--border)]"}`}
        >
          <span className={`absolute top-1 left-1 h-5 w-5 rounded-full bg-white shadow transition-transform ${on ? "translate-x-5" : ""}`} />
        </button>
      </div>
    </section>
  );
}
